<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

defineProps({
    title: { type: String, default: '' },
    text: { type: String, default: '' },
    timeDateStyle: { type: Boolean, default: false },
    iconTextColor: {
        type: String,
        required: false,
        default: 'text-dashboard-primary-grey'
    },
    slots: {
        type: Boolean,
        default: false
    }
});

const isTooltipVisible = ref(false);
const tooltipStyles = ref({ top: '0px', left: '0px' });

const trigger = ref(null);
const tooltip = ref(null);
const isTouch = ref(false);

const showTooltip = (evt) => {
    if (!evt) return;

    isTooltipVisible.value = true;
    nextTick(() => updateTooltipPosition(evt));
};

const hideTooltip = () => {
    isTooltipVisible.value = false;
};

const toggleTooltip = (evt) => {
    if (isTooltipVisible.value) {
        hideTooltip();
    } else {
        showTooltip(evt);
    }
};

const onMouseMove = (evt) => {
    if (isTouch.value) return; // On touch devices we use click only

    if (!isTooltipVisible.value) showTooltip(evt);
    else updateTooltipPosition(evt);
};

const onMouseLeave = () => {
    if (isTouch.value) return;

    hideTooltip();
};

const updateTooltipPosition = (evt) => {
    const el = tooltip.value;

    if (!el) return;

    const tooltipHeight = el.offsetHeight;
    const tooltipWidth = el.offsetWidth || 220;
    const offsetY = 20;

    const cursorY = evt.clientY + window.scrollY;
    const cursorX = evt.clientX + window.scrollX;

    let top = cursorY - tooltipHeight - offsetY;
    let left = cursorX - tooltipWidth / 2;

    const minPad = 8;
    const viewportWidth = document.documentElement.clientWidth;
    const maxLeft = window.scrollX + viewportWidth - tooltipWidth - minPad;

    if (left < minPad) left = minPad;

    if (left > maxLeft) left = maxLeft;

    if (top < window.scrollY + minPad) {
        // place below cursor if not enough room above
        top = cursorY + offsetY;
    }

    tooltipStyles.value = { top: `${top}px`, left: `${left}px` };
};

const onDocumentClick = (e) => {
    if (!isTooltipVisible.value) return;

    if (trigger.value && !trigger.value.contains(e.target) && tooltip.value && !tooltip.value.contains(e.target)) {
        hideTooltip();
    }
};

const onScrollHide = () => {
    if (isTooltipVisible.value) hideTooltip();
};

onMounted(() => {
    isTouch.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    document.addEventListener('click', onDocumentClick, { passive: true });
    window.addEventListener('scroll', onScrollHide, { passive: true });
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
    window.removeEventListener('scroll', onScrollHide);
});
</script>

<template>
    <div
        ref="trigger"
        class="inline-flex"
        role="button"
        tabindex="0"
        aria-haspopup="true"
        :aria-expanded="isTooltipVisible.toString()"
        @mousemove="onMouseMove"
        @mouseleave="onMouseLeave"
        @click="toggleTooltip"
        @keydown.enter.prevent="toggleTooltip"
        @keydown.space.prevent="toggleTooltip"
    >
        <i v-if="!timeDateStyle" class="fi fi-rr-info text-xs" :class="[iconTextColor ? iconTextColor : 'text-dashboard-primary-grey']"></i>
        <i v-else class="fi fi-rr-clock-five text-xs" :class="[iconTextColor ? iconTextColor : 'text-dashboard-primary-grey']"></i>

        <teleport to="body">
            <transition
                enter-active-class="transition-opacity duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-300"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <span
                    v-if="isTooltipVisible"
                    ref="tooltip"
                    role="tooltip"
                    class="absolute bg-white text-xs rounded-xl py-1 px-2 z-[9999] border-t-2 border-[#353CEE] max-w-[370px] flex flex-col shadow-xl"
                    :style="tooltipStyles"
                >
                    <div class="flex mb-1 gap-1" :class="timeDateStyle ? 'text-[#353CEE]' : 'text-dashboard-primary-grey'">
                        <i v-if="!timeDateStyle" class="fi fi-rr-info text-[#353CEE] text-lg mr-2 align-top"></i>
                        <i v-else class="fi fi-rr-clock-five text-[#353CEE] text-md mr-2"></i>
                        <div v-if="!slots">
                            <h5 class="font-bold text-sm">{{ title }}</h5>
                            <span class="text-black text-sm">{{ text }}</span>
                        </div>
                        <div v-else><slot></slot></div>
                    </div>
                </span>
            </transition>
        </teleport>
    </div>
</template>
